/**
 * The tour's frame: where the person is, and the one way out. Fixed at the
 * top of the window for the whole tour; the last step has no way out but
 * finishing.
 */

import { createPortal } from "react-dom";
import { TOUR_UI_ATTR } from "./chrome";

export function TourPill({
  step,
  total,
  working,
  onExit,
}: {
  step: number;
  total: number;
  /** A scene is taking long to set and there is no card to say so. */
  working: boolean;
  /** Absent on the last step. */
  onExit?: () => void;
}) {
  return createPortal(
    <>
      <style>{PILL_STYLES}</style>
      <div
        {...{ [TOUR_UI_ATTR]: "" }}
        data-tour-pill=""
        className="_co_onb _co_tour_pill"
        role="group"
        aria-label="Guided tour"
      >
        <span className="_co_tour_pill_dot" aria-hidden />
        <span data-tour-pill-step="">
          <span>Tour · step </span>
          <span>{step}</span>
          <span> of </span>
          <span>{total}</span>
        </span>
        {working && (
          <span data-tour-pill-working="" className="_co_tour_pill_muted">
            <span>Still working…</span>
          </span>
        )}
        {onExit && (
          <button
            type="button"
            data-tour-exit=""
            className="_co_tour_pill_exit"
            onClick={onExit}
          >
            <span>Exit tour</span>
          </button>
        )}
      </div>
    </>,
    document.body,
  );
}

const PILL_STYLES = `
  ._co_tour_pill {
    position: fixed;
    top: 7px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 56;
    height: 30px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 0 6px 0 12px;
    border-radius: 999px;
    background: var(--co-paper);
    border: 1px solid var(--co-border);
    box-shadow: 0 1px 2px rgba(15, 12, 8, 0.06), 0 4px 14px rgba(15, 12, 8, 0.10);
    color: var(--co-fg-muted);
    font: 500 12px/1 ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Inter', 'Geist', sans-serif;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    user-select: none;
  }
  .dark ._co_tour_pill { background: rgba(26, 26, 22, 0.96); }
  ._co_tour_pill:not(:has(._co_tour_pill_exit)) { padding-right: 12px; }
  ._co_tour_pill_dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--co-rust);
    flex-shrink: 0;
  }
  ._co_tour_pill_muted { opacity: 0.8; }
  ._co_tour_pill_exit {
    appearance: none;
    height: 22px;
    padding: 0 9px;
    border: none;
    border-left: 1px solid var(--co-border);
    border-radius: 0 999px 999px 0;
    background: none;
    font: inherit;
    color: var(--co-fg-muted);
    cursor: pointer;
  }
  ._co_tour_pill_exit:hover { color: var(--co-fg); text-decoration: underline; text-underline-offset: 3px; }
  ._co_tour_pill_exit:focus-visible { outline: 2px solid var(--co-rust); outline-offset: 1px; }
`;
